'use client';

interface AgeRangePickerProps {
  selectedAgeRange?: string;
  onSelect: (ageRange: string | undefined) => void;
  availableRanges?: string[];
  totalCount?: number;
  rangeCounts?: Record<string, number>;
  labels?: {
    allAges?: string;
    years?: string;
  };
}

const DEFAULT_AGE_RANGES = ['0-1', '1-2', '3-4', '5-6', '7-8', '9-10'];

export function AgeRangePicker({ 
  selectedAgeRange, 
  onSelect, 
  availableRanges,
  totalCount,
  rangeCounts,
  labels = { allAges: 'All Ages', years: 'years' }
}: AgeRangePickerProps) {
  const ageRanges = availableRanges && availableRanges.length > 0 ? availableRanges : DEFAULT_AGE_RANGES;

  return (
    <div className="age-range-picker">
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.5rem',
        justifyContent: 'center',
      }}>
        <button
          onClick={() => onSelect(undefined)}
          className="age-range-chip"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: 'var(--radius-full)',
            border: selectedAgeRange === undefined ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
            background: selectedAgeRange === undefined ? 'var(--color-primary-light)' : 'white',
            color: selectedAgeRange === undefined ? 'var(--color-primary)' : 'var(--color-text-secondary)',
            fontSize: '0.875rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all var(--transition-fast)',
            fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => {
            if (selectedAgeRange !== undefined) {
              e.currentTarget.style.borderColor = 'var(--color-primary)';
            }
          }}
          onMouseLeave={(e) => {
            if (selectedAgeRange !== undefined) {
              e.currentTarget.style.borderColor = 'var(--color-border)';
            }
          }}
        >
          {labels.allAges}
          {totalCount !== undefined && (
            <span style={{
              background: selectedAgeRange === undefined ? 'var(--color-primary)' : 'var(--color-border)',
              color: selectedAgeRange === undefined ? 'white' : 'var(--color-text-secondary)',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: '0.75rem',
              fontWeight: 700,
              minWidth: '1.5rem',
              textAlign: 'center',
            }}>
              {totalCount}
            </span>
          )}
        </button>
        {ageRanges.map((range) => {
          const count = rangeCounts?.[range];
          return (
            <button
              key={range}
              onClick={() => onSelect(range)}
              className="age-range-chip"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                borderRadius: 'var(--radius-full)',
                border: selectedAgeRange === range ? '2px solid var(--color-primary)' : '2px solid var(--color-border)',
                background: selectedAgeRange === range ? 'var(--color-primary-light)' : 'white',
                color: selectedAgeRange === range ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                fontSize: '0.875rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all var(--transition-fast)',
                fontFamily: 'inherit',
              }}
              onMouseEnter={(e) => {
                if (selectedAgeRange !== range) {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedAgeRange !== range) {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                }
              }}
            >
              {range} {labels.years}
              {count !== undefined && (
                <span style={{
                  background: selectedAgeRange === range ? 'var(--color-primary)' : 'var(--color-border)',
                  color: selectedAgeRange === range ? 'white' : 'var(--color-text-secondary)',
                  padding: '0.125rem 0.5rem',
                  borderRadius: '9999px',
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  minWidth: '1.5rem',
                  textAlign: 'center',
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <style jsx>{`
        @media (max-width: 768px) {
          .age-range-chip {
            font-size: 0.8125rem !important;
            padding: 0.4375rem 0.875rem !important;
          }
        }
      `}</style>
    </div>
  );
}

